import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { authApi, readApiError } from '@/lib/auth-api';

type State = 'idle' | 'verifying' | 'ok' | 'failed';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>(token ? 'verifying' : 'idle');
  const [err, setErr] = useState<string | null>(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resentOk, setResentOk] = useState(false);

  // Verification tokens are single-use: the backend nulls the token the moment
  // it's consumed. React 18 StrictMode runs effects twice in dev, which would
  // fire a second verify with the now-consumed token and surface a spurious
  // "invalid or expired" error. The ref survives StrictMode's double-invoke
  // (same component instance), so we only ever hit the endpoint once per token.
  // A `cancelled` flag is deliberately NOT used: StrictMode's cleanup between
  // the two invokes would trip it and suppress the lone request's result,
  // stranding the page on "verifying". After the double-invoke settles the
  // component is mounted, so setting state when the request resolves is safe.
  const verifiedToken = useRef<string | null>(null);

  useEffect(() => {
    if (!token) return;
    if (verifiedToken.current === token) return;
    verifiedToken.current = token;
    (async () => {
      try {
        await authApi.verifyEmail(token);
        setState('ok');
        setTimeout(() => nav('/app', { replace: true }), 1500);
      } catch (e) {
        setErr(readApiError(e, t('auth.error_verify_failed')));
        setState('failed');
      }
    })();
  }, [token, nav, t]);

  if (state === 'ok') {
    return (
      <AuthLayout title={t('auth.verify_ok_title')}>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-emerald-600 text-white shrink-0">
            <CheckCircle2 className="h-4 w-4" />
          </span>
          <div>
            <p className="text-[14px] text-emerald-900 font-medium">{t('auth.verify_ok_title')}</p>
            <p className="mt-1 text-[13px] text-emerald-800/85 leading-relaxed">{t('auth.verify_ok_body')}</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={state === 'verifying' ? t('auth.verify_running_title') : t('auth.verify_failed_title')}
      subtitle={state === 'verifying' ? undefined : t('auth.verify_failed_subtitle')}
    >
      {state === 'verifying' && (
        <p className="text-[13px] text-muted-foreground">{t('auth.verify_running_body')}</p>
      )}

      {state === 'failed' && (
        <>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-amber-600 text-white shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div>
              <p className="text-[14px] text-amber-900 font-medium">{err ?? t('auth.error_verify_failed')}</p>
              <p className="mt-1 text-[13px] text-amber-800/85 leading-relaxed">{t('auth.verify_failed_help')}</p>
            </div>
          </div>

          <form
            className="mt-5 flex flex-col sm:flex-row items-start sm:items-end gap-2"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!resendEmail) return;
              try {
                await authApi.resendVerification(resendEmail);
                setResentOk(true);
              } catch (e) {
                setErr(readApiError(e, t('auth.error_resend_failed')));
              }
            }}
          >
            <label className="flex-1 w-full">
              <span className="text-[11px] uppercase tracking-[0.16em] font-medium text-muted-foreground">{t('auth.email_label')}</span>
              <input
                type="email"
                placeholder="cfo@acme.co"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="mt-1.5 block w-full h-11 rounded-md border border-border bg-card px-3 text-[14px] focus:outline-none focus:ring-2 focus:ring-foreground/15"
              />
            </label>
            <button
              type="submit"
              className="h-11 px-5 rounded-md bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
            >
              {t('auth.resend_verification')}
            </button>
          </form>

          {resentOk && (
            <p className="mt-3 text-[12.5px] text-emerald-700">{t('auth.resend_sent', { email: resendEmail })}</p>
          )}
        </>
      )}

      <p className="mt-7 text-[13px] text-muted-foreground">
        <Link to="/login" className="hover:underline">← {t('auth.back_to_login')}</Link>
      </p>
    </AuthLayout>
  );
}
